import { Dispatch } from 'redux';

import appEventTypes from 'shared/store/app/actionTypes';
import { defaultDataLayer } from 'constants/dataLayer';
import statusCodes from 'shared/constants/statusCodes';
import { createActiveFilterObjectFromURL } from '@nykaa/listing-shared/utils/filter';
import { actionForSSRDataLayer } from 'shared/store/dataLayer/action';
import actionTypes from './actionTypes';
import { getSearchProducts } from './service';
import { getDataLayer } from './helpers';
import { pushData } from 'helpers/utils/dataLayer';
import PageTypes from 'constants/pageTypes';
import { SEARCH_COMPLETE_TRACKING, SYSTEM } from 'shared/constants';
import { getWidgets } from 'store/adPlatform/service';
import { logger } from '@nykaa/logger';
import { getDnPageTypeFromWidgetData } from 'shared/helpers';
import { getPLPAppliedFilters } from '@nykaa/listing-shared/utils/filter';
import { QUERY_PARAM_EXP } from 'constants/experiments';
import { getEligibleVariantName } from 'helpers/experimentV2';
import { State } from 'store/types';
import { InteractionData } from 'store/categoryListing/types';
import { sendSingleEvent } from 'helpers/impressions/sendSingleRetinaEvent';
import { transformSearchListingData } from './transformers';

export interface FetchSearchProduct {
  params: any;
  queryString: string;
  isFilterOrSortUpdate?: boolean;
  isRedirectEnabled?: boolean;
  priveBand?: string;
  isConversationSearch?: boolean;
}

export const changeFilterApiStatus =
  (isApiFailed?: boolean) => (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.CHANGE_FILTER_API_STATUS,
      payload: Boolean(isApiFailed),
    });
  };

export const fetchSearchProducts =
  (data: FetchSearchProduct) => async (dispatch: Dispatch, getState: any) => {
    const {
      params,
      isConversationSearch = false,
      queryString = '',
      isFilterOrSortUpdate = false,
      isRedirectEnabled = false,
    } = data;
    dispatch({
      type: actionTypes.FETCH_PROGRESS,
    });

    const { search } = params;
    // Need to find a better solution to handle this use case
    const state: State = getState();
    const variant = getEligibleVariantName(state, QUERY_PARAM_EXP);
    const { apiQueryParamConfigV2 } = state.remoteConfig.configs;
    let modifiedParams = {
      ...params,
    };
    if (variant && apiQueryParamConfigV2[variant]) {
      modifiedParams = {
        ...params,
        ...apiQueryParamConfigV2[variant],
      };
    }
    // Need to find a better solution to handle this use case

    try {
      const transformedListingData = await getSearchProducts(
        modifiedParams,
        false
      );
      const dnHybridPageType = getDnPageTypeFromWidgetData(
        transformedListingData
      );
      let suggestionType = '';
      if (transformedListingData.isRedirect) {
        if (!isRedirectEnabled) {
          return transformedListingData;
        } else {
          dispatch({
            type: actionTypes.IS_REDIRECT,
            payload: {
              redirect: transformedListingData,
            },
          });
        }
      } else if (
        params?.price_range_filter &&
        transformedListingData.totalFound === 0
      ) {
        dispatch({
          type: actionTypes.FETCH_EMPTY_RANGE_ERROR,
        });
      } else {
        const activeFilters = createActiveFilterObjectFromURL(
          transformedListingData.filters,
          queryString,
          transformedListingData.guides
        );
        dispatch({
          type: actionTypes.FETCH_SUCCESS,
          payload: {
            listingData: transformedListingData,
            pageNumber: 1,
            searchTerm: search,
            activeFilters,
          },
        });
        suggestionType = activeFilters.suggestionType;
      }

      if (!isFilterOrSortUpdate) {
        // * handling data layer
        const searchTrackingData = {
          errorCode: null,
          params,
          listingData: transformedListingData,
          suggestionType,
          listingPageType: 'search',
          analyticsSdkData: {
            plpDnHybridPageType: dnHybridPageType,
            plpSearchTerm: params?.search,
          },
          isConversationSearch,
        };
        const dataLayer = getDataLayer(searchTrackingData);
        if (__SERVER__) {
          dispatch(actionForSSRDataLayer(dataLayer));
        } else {
          const defaultData = defaultDataLayer();
          pushData(
            {
              ...defaultData,
              ...dataLayer,
              ...(__BROWSER__ && { spaPageView: Date.now() }),
            },
            true
          );
        }
        // * handling page type for search listing
        dispatch({
          type: appEventTypes.APP_REDUCER_SET_PAGE,
          payload: PageTypes.SEARCH_LISTING,
        });
      } else {
        if (!__SERVER__) {
          sendSingleEvent({
            event_type: SYSTEM,
            event_name: SEARCH_COMPLETE_TRACKING,
            sort: transformedListingData?.sort,
            filters: getPLPAppliedFilters(params),
            result_count: transformedListingData?.totalFound || '',
            tracking_metadata: transformedListingData.trackingMetadata || {},
            entity_coverage: transformedListingData?.entityCoverage || '',
            entity_type: transformedListingData?.entityType || '',
            entity_details: transformedListingData?.entityValues || '',
            search_term: search,
            page_search_term: search,
          });
          const searchCompleteData = {
            totalFound: transformedListingData.totalFound,
            trackingMetadata: transformedListingData.trackingMetadata,
            searchTerm: search,
          };
          pushData(searchCompleteData);
        }
      }

      return transformedListingData;
    } catch (error) {
      let statusCode = statusCodes.ERROR;
      logger.info('Error in Search Listing action', error);
      if (error.isAxiosError && error.response?.status) {
        statusCode = error.response.status;
      }
      const dataLayer = getDataLayer({ errorCode: statusCode, params });
      changeFilterApiStatus(true)(dispatch);
      dispatch({
        type: appEventTypes.APP_REDUCER_STATUS_CODE,
        payload: statusCode,
      });

      if (__SERVER__) {
        dispatch({
          type: actionTypes.FETCH_ERROR,
        });
        dispatch(actionForSSRDataLayer(dataLayer));
      } else {
        dispatch({
          type: actionTypes.FETCHING_ERROR_CLIENT,
        });
        pushData(dataLayer);
        if (isFilterOrSortUpdate) {
          throw error;
        }
      }
    }
  };

export const convSearchListing =
  (responseData: any, searchTerm: string = '') =>
  (dispatch: Dispatch, getState: any) => {
    dispatch({
      type: actionTypes.CONV_SEARCH_FETCH_PROGRESS,
    });

    try {
      const transformedListingData = transformSearchListingData(responseData);
      const processTransformedData = (data: any) => {
        const listingData = data;

        const currentState = getState();
        const currentActiveFilters =
          currentState?.searchListingPage?.activeFilters || {};

        let activeFilters;
        if (searchTerm) {
          const filteredActiveFilters = Object.keys(
            currentActiveFilters
          ).reduce((acc, key) => {
            if (key === 'q' || key === 'root') {
              acc[key] = currentActiveFilters[key];
            }
            return acc;
          }, {} as any);

          activeFilters = {
            ...filteredActiveFilters,
            q: searchTerm,
          };
          if (typeof window !== 'undefined') {
            const currentUrl = new URL(window.location.href);
            const newParams = new URLSearchParams(currentUrl.search);
            newParams.set('q', searchTerm);
            if (filteredActiveFilters.root) {
              newParams.set('root', filteredActiveFilters.root);
            }
            const queryString = newParams.toString();
            const newUrl = `${currentUrl.pathname}${
              queryString ? `?${queryString}` : ''
            }`;
            window.history.replaceState(null, '', newUrl);
          }
        } else {
          const baseActiveFilters =
            Object.keys(currentActiveFilters).length > 0
              ? currentActiveFilters
              : createActiveFilterObjectFromURL(
                  listingData.filters || [],
                  '',
                  listingData.guides || []
                );

          activeFilters = Object.keys(baseActiveFilters).reduce((acc, key) => {
            if (key === 'q' || key === 'root') {
              acc[key] = baseActiveFilters[key];
            }
            return acc;
          }, {} as any);
        }
        const finalSearchTerm =
          searchTerm || activeFilters?.q || activeFilters?.search || '';

        dispatch({
          type: actionTypes.CONV_SEARCH_FETCH_SUCCESS,
          payload: {
            listingData,
            pageNumber: 1,
            searchTerm: finalSearchTerm,
            activeFilters,
          },
        });

        return data;
      };

      // Check if transformedListingData is a promise or direct data
      const transformedDataPromise =
        transformedListingData instanceof Promise
          ? transformedListingData
          : Promise.resolve(transformedListingData);

      return transformedDataPromise.then(processTransformedData);
    } catch (error) {
      logger.info('Error in Conv Search Listing action', error);
      dispatch({
        type: actionTypes.CONV_SEARCH_FETCH_ERROR,
        payload: error,
      });
      throw error;
    }
  };

export const resetRedirection = () => (dispatch: Dispatch) => {
  dispatch({
    type: actionTypes.RESET_REDIRECT,
  });
};

export const changeFilter =
  (newFilter: any, isClearAll = false) =>
  (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.FILTER_CHANGE,
      payload: {
        newFilter,
        isClearAll,
      },
    });
  };

export const resetFilters =
  (newFilters: UserDefinedObject) => (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.RESET_FILTERS,
      payload: newFilters,
    });
  };

export const changeSort = (newSort: string) => (dispatch: Dispatch) => {
  dispatch({
    type: actionTypes.SORT_CHANGE,
    payload: newSort,
  });
};

export const changePage = () => (dispatch: Dispatch) => {
  dispatch({
    type: actionTypes.CHANGE_PAGE,
  });
};

export const setInteraction =
  (value: InteractionData) => (dispatch: Dispatch) =>
    dispatch({
      type: actionTypes.CLICK_INTERACTION,
      payload: value,
    });

export const fetchNextPage =
  (params: any) => async (dispatch: Dispatch, getState: any) => {
    dispatch({
      type: actionTypes.FETCH_PAGE_START,
    });

    // Need to find a better solution to handle this use case
    const state: State = getState();
    const variant = getEligibleVariantName(state, QUERY_PARAM_EXP);
    const { apiQueryParamConfigV2 } = state.remoteConfig.configs;
    let modifiedParams = {
      ...params,
    };
    if (variant && apiQueryParamConfigV2[variant]) {
      modifiedParams = {
        ...params,
        ...apiQueryParamConfigV2[variant],
      };
    }
    // Need to find a better solution to handle this use case

    try {
      const transformedPageData = await getSearchProducts(modifiedParams, true);
      dispatch({
        type: actionTypes.FETCH_PAGE_SUCCESS,
        payload: transformedPageData,
      });
    } catch (error) {
      setTimeout(() => {
        dispatch({
          type: actionTypes.FETCH_PAGE_ERROR,
          payload: error,
        });
      }, 200);
    }
  };

export const fetchEmptyStateWidgets =
  (params: any, isPaginationEnabled?: boolean) => (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.FETCH_EMPTY_STATE_PROGRESS,
    });
    const requestParams = {
      ...params,
    };
    return getWidgets(requestParams, '', isPaginationEnabled)
      .then(({ widgets, remaining_inventories_ids }) => {
        dispatch({
          type: actionTypes.FETCH_EMPTY_STATE_SUCCESS,
          payload: {
            widgets,
            remaining_inventories_ids,
          },
        });
      })
      .catch((err) => {
        dispatch({
          type: actionTypes.FETCH_EMPTY_STATE_FAILED,
        });
        logger.error(err, 'Error in Fetch Widgets action');
      });
  };

export const filterOpen =
  (isOpen: boolean, activeFilterType: string = '') =>
  (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.IS_FILTER_OPEN,
      payload: {
        isFilterOpen: isOpen,
        activeFilterType,
      },
    });
  };
